import React, { useEffect, useState } from "react";
import { cn } from "../../lib/utils";
import { CommonLayoutProps } from "./types";
import CommonSidebar from "./CommonSidebar";
import CommonTopBar from "./CommonTopBar";
import { NotificationSidebar } from "../../client/components/Sidebar/NotificationSidebar";
import BottomNav from "./BottomNav";

export default function CommonLayout({
  children,
  config,
  showViewSwitcher = false,
  activeView = "cards",
  onViewChange,
  forceSidebarCollapse = false,
}: CommonLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window === "undefined") {
      return !forceSidebarCollapse;
    }
    const isDesktop = window.innerWidth >= 1024;
    return isDesktop && !forceSidebarCollapse;
  });

  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      setIsSidebarOpen(isDesktop && !forceSidebarCollapse);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [forceSidebarCollapse]);

  const handleNavigate = () => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <div
      className="h-screen bg-background relative overflow-hidden"
      style={
        {
          "--header-height": config.headerHeight,
          "--sidebar-width": isSidebarOpen ? `${config.sidebarWidth}px` : "0px",
        } as React.CSSProperties
      }
    >
      <NotificationSidebar />
      <CommonSidebar
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        config={config}
        onNavigate={handleNavigate}
      />

      {/* Mobile overlay when sidebar is open - Higher z-index to cover top bar but below sidebar */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[55] lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <CommonTopBar
        isSidebarOpen={isSidebarOpen}
        onSidebarToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        config={config}
        activeView={showViewSwitcher ? activeView : undefined}
        onViewChange={showViewSwitcher ? onViewChange : undefined}
      />

      <div
        className={cn(
          "flex flex-col h-screen overflow-hidden transition-all duration-300",
          `pt-[var(--header-height)]`,
          "pb-20 lg:pb-0", // Add padding-bottom for mobile BottomNav
          isSidebarOpen ? `lg:ml-[var(--sidebar-width)]` : "ml-0",
        )}
      >
        <main className="flex-1 overflow-y-auto w-full h-full">{children}</main>
      </div>
      <BottomNav />
    </div>
  );
}
